import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Award, Download, Eye, Check, Paintbrush, LayoutGrid, Save, ZoomIn, ZoomOut } from 'lucide-react';
import { CertificateTemplateSelector, CertificatePreview, CertificateEditor, defaultTemplates, type CertificateTemplate, type CertificateData } from '@/components/certificates';
import { useCertificateGenerator } from '@/hooks/useCertificateGenerator';
import { useCertificateTemplates } from '@/hooks/useCertificateTemplates';
import { toast } from 'sonner';

const sampleData: CertificateData = {
  recipientName: 'Jane Smith',
  pathwayName: 'Sales Excellence Program',
  certificationName: 'Certified Sales Professional',
  completionDate: new Date(),
  score: 92,
  companyName: 'MEDD Training',
  issuerName: 'Director of Training'
};

export default function CertificateManagement() {
  const [selectedTemplate, setSelectedTemplate] = useState<CertificateTemplate>(defaultTemplates[0]);
  const [editingTemplate, setEditingTemplate] = useState<CertificateTemplate>(defaultTemplates[0]);
  const [activeTab, setActiveTab] = useState('templates');
  const [zoom, setZoom] = useState(1);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [saveName, setSaveName] = useState('');

  const { generateCertificatePdf } = useCertificateGenerator();
  const {
    allTemplates,
    uploading,
    saveTemplate,
    deleteTemplate,
    uploadLogo,
    uploadSignature,
  } = useCertificateTemplates();

  const handleDownloadSample = async () => {
    try {
      const templateToUse = activeTab === 'customize' ? editingTemplate : selectedTemplate;
      await generateCertificatePdf(templateToUse, sampleData);
      toast.success('Sample certificate downloaded!');
    } catch (error) {
      toast.error('Failed to generate certificate');
    }
  };

  const handleTemplateSelect = useCallback((template: CertificateTemplate) => {
    setSelectedTemplate(template);
    setEditingTemplate({ ...template });
  }, []);

  const handleEditorChange = useCallback((updated: CertificateTemplate) => {
    setEditingTemplate(updated);
  }, []);

  const handleSaveTemplate = async () => {
    const name = saveName.trim() || editingTemplate.name;
    const saved = await saveTemplate(editingTemplate, name);
    if (saved) {
      setSelectedTemplate(saved);
      setEditingTemplate(saved);
      setSaveDialogOpen(false);
      setSaveName('');
    }
  };

  const handleDeleteTemplate = async (templateId: string) => {
    await deleteTemplate(templateId);
    // If the deleted template was selected, reset to first default
    if (selectedTemplate.id === templateId) {
      setSelectedTemplate(defaultTemplates[0]);
      setEditingTemplate(defaultTemplates[0]);
    }
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (tab === 'customize') {
      setEditingTemplate({ ...selectedTemplate });
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Award className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold">Certificate Management</h1>
            <p className="text-muted-foreground">
              Design and manage certificates for your learning pathways
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {activeTab === 'customize' && (
            <Button
              variant="outline"
              onClick={() => {
                setSaveName(editingTemplate.isCustom ? editingTemplate.name : `Custom ${editingTemplate.name}`);
                setSaveDialogOpen(true);
              }}
            >
              <Save className="w-4 h-4 mr-2" />
              Save Template
            </Button>
          )}
          <Button onClick={handleDownloadSample}>
            <Download className="w-4 h-4 mr-2" />
            Download Sample
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="mb-6">
          <TabsTrigger value="templates" className="flex items-center gap-1.5">
            <LayoutGrid className="w-4 h-4" />
            Template Gallery
          </TabsTrigger>
          <TabsTrigger value="customize" className="flex items-center gap-1.5">
            <Paintbrush className="w-4 h-4" />
            Customize
          </TabsTrigger>
          <TabsTrigger value="preview" className="flex items-center gap-1.5">
            <Eye className="w-4 h-4" />
            Preview
          </TabsTrigger>
        </TabsList>

        {/* Template Gallery Tab */}
        <TabsContent value="templates">
          <CertificateTemplateSelector
            value={selectedTemplate.id}
            onChange={handleTemplateSelect}
            templates={allTemplates}
            onDelete={handleDeleteTemplate}
          />
        </TabsContent>

        {/* Customize Tab - Side by Side Layout */}
        <TabsContent value="customize">
          <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
            {/* Left Panel - Editor Controls */}
            <div>
              <CertificateEditor
                template={editingTemplate}
                onChange={handleEditorChange}
                onLogoUpload={uploadLogo}
                onSignatureUpload={uploadSignature}
                uploading={uploading}
              />
            </div>

            {/* Right Panel - Live Preview */}
            <div>
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <Eye className="w-4 h-4" />
                      Live Preview
                    </CardTitle>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => setZoom((z) => Math.max(0.5, z - 0.1))}
                        disabled={zoom <= 0.5}
                      >
                        <ZoomOut className="w-3.5 h-3.5" />
                      </Button>
                      <span className="text-xs text-muted-foreground w-10 text-center">
                        {Math.round(zoom * 100)}%
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => setZoom((z) => Math.min(1.5, z + 0.1))}
                        disabled={zoom >= 1.5}
                      >
                        <ZoomIn className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-center overflow-auto">
                    <div
                      style={{
                        transform: `scale(${zoom})`,
                        transformOrigin: 'top center',
                        transition: 'transform 0.2s ease',
                      }}
                      className="w-full max-w-2xl"
                    >
                      <CertificatePreview
                        template={editingTemplate}
                        data={{
                          ...sampleData,
                          companyName: editingTemplate.companyName || sampleData.companyName,
                          issuerName: editingTemplate.issuerName || sampleData.issuerName,
                        }}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* Preview Tab */}
        <TabsContent value="preview">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Full Preview: {selectedTemplate.name}
                </CardTitle>
                <Badge variant="outline" className="flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  Selected Template
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex justify-center">
                <div className="w-full max-w-2xl">
                  <CertificatePreview
                    template={selectedTemplate}
                    data={sampleData}
                  />
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-muted rounded-lg">
                <h4 className="font-medium mb-2">Template Details</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Primary Color:</span>
                    <div className="flex items-center gap-2 mt-1">
                      <div 
                        className="w-4 h-4 rounded-full border"
                        style={{ backgroundColor: selectedTemplate.primaryColor }}
                      />
                      <span>{selectedTemplate.primaryColor}</span>
                    </div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Accent Color:</span>
                    <div className="flex items-center gap-2 mt-1">
                      <div 
                        className="w-4 h-4 rounded-full border"
                        style={{ backgroundColor: selectedTemplate.accentColor }}
                      />
                      <span>{selectedTemplate.accentColor}</span>
                    </div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Font Family:</span>
                    <p className="font-medium capitalize mt-1">{selectedTemplate.fontFamily}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Border Style:</span>
                    <p className="font-medium capitalize mt-1">{selectedTemplate.borderStyle}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Save Template Dialog */}
      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Certificate Template</DialogTitle>
            <DialogDescription>
              {editingTemplate.isCustom
                ? 'Update your existing custom template or save as a new one.'
                : 'Save your customizations as a new template that your team can use.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Template Name</Label>
              <Input
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder="My Custom Template"
                autoFocus
              />
            </div>
            {/* Mini preview */}
            <div className="border rounded-lg p-3">
              <p className="text-xs text-muted-foreground mb-2">Preview</p>
              <div className="w-full max-w-xs mx-auto">
                <CertificatePreview
                  template={editingTemplate}
                  data={sampleData}
                  scale={0.5}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveTemplate}>
              <Save className="w-4 h-4 mr-2" />
              {editingTemplate.isCustom ? 'Update Template' : 'Save Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
